import React, { useContext, useMemo } from "react";
import { Button, Heading, Text } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../context/EditSessionsContext";
import { isWeekLockedForSession, WeekLockContext } from "../../utils/isWeekLocked";
import { useForkOrchestration } from "../../hooks/useForkOrchestration";
import { useIsEditorLocked } from "../../hooks/useIsEditorLocked";
import styles from "./weeksForkCard.module.scss";

interface WeeksForkCardProps {
  weekLockContext: WeekLockContext;
}

const WeeksForkCard: React.FC<WeeksForkCardProps> = ({ weekLockContext }) => {
  const { state } = useContext(AppContext);
  const { t } = useTranslation();
  const { runFork } = useForkOrchestration();
  const isLocked = useIsEditorLocked();

  // The lock context resolves the current selection against saved/created
  // editions, so the fork uses an edited weekday instead of a stale card value.
  const sessionsToFork = useMemo(
    () =>
      (weekLockContext.selectedSessions ?? []).filter(
        session =>
          !!session?.id && !session.id.includes("-") && !session.isNew && !session.isCloned,
      ),
    [weekLockContext.selectedSessions],
  );

  const weeksThatStayLabel = useMemo(() => {
    const intervals = state?.form?.editedSessions?.intervals ?? [];

    return intervals
      .filter(week => {
        const isOriginallyAssigned = !!weekLockContext.originalSelectedWeekIds?.[week.id];
        const isUncheckedOriginallyAssigned = isOriginallyAssigned && !week.checked;
        const isCheckedAndLocked =
          !!week.checked &&
          sessionsToFork.some(session => isWeekLockedForSession(week, session, weekLockContext));

        return isUncheckedOriginallyAssigned || isCheckedAndLocked;
      })
      .map(week => week.name)
      .join(", ");
  }, [state?.form?.editedSessions?.intervals, sessionsToFork, weekLockContext]);

  // With `can_edit_past_sessions` the threshold no longer locks anything, so
  // the only weeks that stay behind are the ones the user unchecked. The copy
  // drops the mention of elapsed and non-editable weeks accordingly.
  const descriptionKey = weekLockContext.canEditPastSessions
    ? "assignation-edit.past-sessions.fork-card.description-past-editable"
    : "assignation-edit.past-sessions.fork-card.description";

  const handleForkClick = () => {
    runFork(
      (week, session) => isWeekLockedForSession(week, session, weekLockContext),
      "assignation-edit.past-sessions.fork-toast",
      sessionsToFork,
    );
  };

  return (
    <div className={styles.weeksForkCard}>
      <Heading className={styles.weeksForkCard_title} type="h4">
        {t("assignation-edit.past-sessions.fork-card.title")}
      </Heading>

      <Text className={styles.weeksForkCard_description}>
        {t(descriptionKey, { weeks: weeksThatStayLabel })}
      </Text>

      <Button disabled={isLocked} onClick={handleForkClick}>
        {t("assignation-edit.past-sessions.fork-card.action-btn")}
      </Button>
    </div>
  );
};

export default WeeksForkCard;
